import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { v4 as uuidv4 } from "uuid";

const CHATWOOT_RETRY_DELAY_MS = 1000;
const CHATWOOT_MAX_RETRY_ATTEMPTS = 5;
const SUPPORT_EMAIL_LINK = "mailto:support@brokerkit.com";

const HelpCenter = () => {
  const retryTimeoutRef = React.useRef(null);

  const clearChatwootRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const openSupportEmail = useCallback(() => {
    window.location.href = SUPPORT_EMAIL_LINK;
  }, []);

  const openChatwootAgentBot = useCallback(
    (attempt = 0) => {
      if (window.$chatwoot?.toggle) {
        clearChatwootRetry();
        window.$chatwoot.toggle("open");
        return;
      }

      if (attempt >= CHATWOOT_MAX_RETRY_ATTEMPTS) {
        openSupportEmail();
        return;
      }

      clearChatwootRetry();

      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        openChatwootAgentBot(attempt + 1);
      }, CHATWOOT_RETRY_DELAY_MS);
    },
    [clearChatwootRetry, openSupportEmail],
  );

  const openSupportChat = useCallback(
    (e) => {
      e.preventDefault();
      openChatwootAgentBot();
    },
    [openChatwootAgentBot],
  );

  React.useEffect(() => () => clearChatwootRetry(), [clearChatwootRetry]);

  const { currentUser } = Rails.helpers;
  const hr = <hr className="my-1" />;

  const LinkWithText = ({
    href = "#",
    children,
    description,
    separater = true,
    target = "_blank",
    onClick,
  }) => (
    <span>
      <a
        className="dropdown-item"
        href={href}
        rel="noopener noreferrer"
        target={target}
        onClick={onClick}
      >
        <h5>{children}</h5>
        {description && <p className="text-muted">{description}</p>}
      </a>
      {separater && hr}
    </span>
  );

  const hasRole = (user, role) =>
    !!user && !!user.role && user.role.name === role;

  const helpArticles = (
    <LinkWithText
      key={uuidv4()}
      href="https://support.brokerkit.app/hc/help/en"
    >
      Help Articles
    </LinkWithText>
  );

  const tutorialVideos = (
    <LinkWithText
      key={uuidv4()}
      href="https://www.youtube.com/playlist?list=PL_ZP6Z-m3hE0nbNOsj2G1AWZ4vP3xtrjq"
    >
      Tutorial Videos
    </LinkWithText>
  );

  const contactViaSupportChat = (
    <LinkWithText key={uuidv4()} onClick={openSupportChat} target="_self">
      Contact Support
    </LinkWithText>
  );

  const scheduleTraining = (
    <LinkWithText key={uuidv4()} href="/home/training-calendar/">
      Training Calendar
    </LinkWithText>
  );

  const customerCommunity = (
    <LinkWithText
      key={uuidv4()}
      href="https://www.facebook.com/groups/brokerkitcommunity"
    >
      Customer Community
    </LinkWithText>
  );

  const suggestFeature = (
    <LinkWithText
      key={uuidv4()}
      href="https://ideas.brokerkit.app/?view=most-wanted"
    >
      Suggest a Feature
    </LinkWithText>
  );

  const podcast = (
    <LinkWithText
      key={uuidv4()}
      href="https://podcasters.spotify.com/pod/show/brokerkit-podcast"
    >
      Podcast
    </LinkWithText>
  );

  const webinar = (
    <LinkWithText
      key={uuidv4()}
      href="https://www.youtube.com/playlist?list=PL_ZP6Z-m3hE33aixw7Q8F_fstGEeE5v3X"
    >
      Webinars
    </LinkWithText>
  );

  const podcastVideos = (
    <LinkWithText
      key={uuidv4()}
      href="https://www.youtube.com/playlist?list=PL_ZP6Z-m3hE1U2QBbflXA1ZdsrX0eUSRl"
      separater={false}
    >
      Podcast Videos
    </LinkWithText>
  );

  const changeLog = (
    <LinkWithText
      key={uuidv4()}
      href="https://ideas.brokerkit.app/?view=completed"
      separater={false}
    >
      Changelog
    </LinkWithText>
  );

  const NestedDropdown = ({ label, children }) => {
    const renderChildren = () =>
      React.Children.map(children, (child, i) =>
        React.cloneElement(child, {
          separater: i < children.length - 1,
        }),
      );

    return (
      <Dropdown drop="right" className="submenu">
        <Dropdown.Toggle
          className="text-nowrap d-flex justify-content-between align-items-center mr-2"
          as="a"
          variant="link"
        >
          <div className="submenu-item mr-2">
            <h5>{label} </h5>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu>{renderChildren()}</Dropdown.Menu>
      </Dropdown>
    );
  };

  let productTrainingLinks;
  let customerSupportLinks;
  let brokerkitAcademyLinks;
  let productUpdatesLinks;

  if (hasRole(currentUser, "agent") || hasRole(currentUser, "affiliate")) {
    productTrainingLinks = [helpArticles];
    customerSupportLinks = [contactViaSupportChat];
  }

  if (
    hasRole(currentUser, "staff") ||
    hasRole(currentUser, "admin") ||
    (!!currentUser && currentUser.admin)
  ) {
    productTrainingLinks = [helpArticles, tutorialVideos, scheduleTraining];
    customerSupportLinks = [contactViaSupportChat];
    brokerkitAcademyLinks = [
      customerCommunity,
      webinar,
      podcast,
      podcastVideos,
    ];
    productUpdatesLinks = [suggestFeature, changeLog];
  }

  return (
    <div
      className="navbar-collapse text-center-sm-down ml-1 mr-2"
      id="navbarSupportedContent"
    >
      <Dropdown as="ul" className="navbar-nav">
        <Dropdown.Toggle
          as="a"
          className="nav-link text-nowrap"
          id="help-center-dropdown"
        >
          <FontAwesomeIcon icon={["far", "fa-life-ring"]} className="mr5" />
          Help Center
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <NestedDropdown label="Product Training">
            {productTrainingLinks}
          </NestedDropdown>

          {customerSupportLinks && (
            <>
              {hr}
              <NestedDropdown label="Customer Support">
                {customerSupportLinks}
              </NestedDropdown>
            </>
          )}

          {brokerkitAcademyLinks && (
            <>
              {hr}
              <NestedDropdown label="Brokerkit Academy">
                {brokerkitAcademyLinks}
              </NestedDropdown>
            </>
          )}

          {productUpdatesLinks && (
            <>
              {hr}
              <NestedDropdown label="Product Updates">
                {productUpdatesLinks}
              </NestedDropdown>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default HelpCenter;
